import React from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Globe } from "lucide-react";

interface Language {
  code: string;
  name: string;
  nativeName: string;
}

// NOTE: Only English is exposed for now. Multi-language support exists
// structurally (translation keys throughout) but is untested, so additional
// locales are intentionally withheld until validated. Re-add here to enable.
const languages: Language[] = [
  { code: "en", name: "English", nativeName: "English" },
];

/** Returns true if multiple languages are available (language switcher should be shown) */
export const hasMultipleLanguages = () => languages.length > 1;

interface LanguageSwitcherProps {
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className,
}) => {
  const { i18n } = useTranslation();

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
  };

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  // Don't render dropdown when only one language is available
  if (languages.length <= 1) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Globe className="h-4 w-4 text-muted-foreground" />
      <Select value={currentLanguage.code} onValueChange={handleLanguageChange}>
        <SelectTrigger className="w-32 h-8 rounded-[8px] bg-[#1a1a1a] border-[#333] text-foreground">
          <SelectValue>{currentLanguage.nativeName}</SelectValue>
        </SelectTrigger>
        <SelectContent className="rounded-[8px] bg-[#1a1a1a] border-[#333]">
          {languages.map((language) => (
            <SelectItem
              key={language.code}
              value={language.code}
              className="rounded-[8px] text-foreground hover:bg-accent/30 focus:bg-accent/30"
            >
              <div className="flex flex-col">
                <span className="font-medium text-foreground">
                  {language.nativeName}
                </span>
                <span className="text-xs text-muted-foreground">
                  {language.name}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
